import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import CompanyInfoForm from './components/CompanyInfoForm';
import PlanSelectionPanel from './components/PlanSelectionPanel';
import AdminAccountForm from './components/AdminAccountForm';
import RegistrationStepIndicator from './components/RegistrationStepIndicator';
import companyRegistrationService from '../../services/companyRegistrationService';
import stripeService from '../../services/stripeService';

const DRAFT_KEY = 'gearcontrol_company_draft';

const withTimeout = (promise, ms = 12000, label = 'request') => {
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error(`[TIMEOUT] ${label} exceeded ${ms}ms`)), ms)
  );
  return Promise.race([promise, timeout]);
};

const CompanyRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [stepLoading, setStepLoading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [stepError, setStepError] = useState('');
  const [success, setSuccess] = useState(false);

  // Load draft from localStorage on mount
  const loadDraft = () => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return null;
  };

  const draft = loadDraft();

  const [companyData, setCompanyData] = useState({
    companyName: draft?.companyName || '',
    legalName: draft?.legalName || '',
    rfc: draft?.rfc || '',
    phone: draft?.phone || '',
    address: draft?.address || '',
    city: draft?.city || '',
    state: draft?.state || '',
    postalCode: draft?.postalCode || ''
  });

  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');

  const [adminData, setAdminData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });

  // Persist Step 1 draft to localStorage whenever companyData changes
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(companyData));
    } catch (e) {}
  }, [companyData]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const data = await companyRegistrationService?.getSubscriptionPlans();
        setPlans(data);
        if (data?.length > 0) {
          const starterPlan = data?.find(p => p?.plan_tier === 'starter') || data?.[0];
          setSelectedPlanId(starterPlan?.id || '');
        }
      } catch (err) {
        console.error('Error loading plans:', err);
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleCompanyChange = (field, value) => {
    setCompanyData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleAdminChange = (field, value) => {
    setAdminData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!companyData?.companyName?.trim()) newErrors.companyName = 'El nombre del taller es requerido';
    if (!companyData?.rfc?.trim()) {
      newErrors.rfc = 'El RFC es requerido';
    } else if (!/^[A-Z]{3,4}[0-9]{6}[A-Z0-9]{3}$/?.test(companyData?.rfc?.toUpperCase())) {
      newErrors.rfc = 'Formato de RFC inválido (ej: GARC850101ABC)';
    }
    if (!companyData?.address?.trim()) newErrors.address = 'La dirección es requerida';
    if (!companyData?.city?.trim()) newErrors.city = 'La ciudad es requerida';
    if (!companyData?.state?.trim()) newErrors.state = 'El estado es requerido';
    if (!companyData?.postalCode?.trim()) {
      newErrors.postalCode = 'El código postal es requerido';
    } else if (!/^[0-9]{5}$/?.test(companyData?.postalCode)) {
      newErrors.postalCode = 'El código postal debe tener 5 dígitos';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!selectedPlanId) newErrors.plan = 'Debes seleccionar un plan de suscripción';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!adminData?.fullName?.trim()) newErrors.fullName = 'El nombre completo es requerido';
    if (!adminData?.email?.trim()) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(adminData?.email)) {
      newErrors.email = 'Ingresa un correo electrónico válido';
    }
    if (!adminData?.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (adminData?.password?.length < 8) {
      newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
    }
    if (!adminData?.confirmPassword) {
      newErrors.confirmPassword = 'Confirma tu contraseña';
    } else if (adminData?.password !== adminData?.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = async () => {
    setSubmitError('');
    setStepError('');

    if (currentStep === 1) {
      console.log('[STEP1_NEXT_CLICK]');

      const isValid = validateStep1();
      if (!isValid) {
        console.log('[STEP1_VALIDATE_FAIL]');
        return;
      }
      console.log('[STEP1_VALIDATE_OK]');

      setStepLoading(true);
      try {
        console.log('[STEP1_UPSERT_START] checking RFC:', companyData?.rfc);
        const rfcExists = await withTimeout(
          companyRegistrationService?.checkRfcExists(companyData?.rfc),
          12000,
          'checkRfcExists'
        );
        if (rfcExists) {
          console.log('[STEP1_UPSERT_ERROR] RFC already exists');
          setErrors(prev => ({ ...prev, rfc: 'Este RFC ya está registrado en el sistema' }));
          return;
        }
        console.log('[STEP1_UPSERT_SUCCESS] RFC available');
        console.log('[ADVANCE_TO_STEP2]');
        setCurrentStep(2);
      } catch (err) {
        console.error('[STEP1_UPSERT_ERROR]', err?.message, err?.code);
        if (err?.message?.includes('TIMEOUT')) {
          setStepError('No se pudo verificar el RFC. Reintenta.');
        } else {
          // Non-critical: RFC check failed but we still allow advancing
          console.warn('[STEP1] RFC check failed, advancing anyway:', err?.message);
          console.log('[ADVANCE_TO_STEP2] (RFC check skipped due to error)');
          setCurrentStep(2);
        }
      } finally {
        setStepLoading(false);
      }
    } else if (currentStep === 2) {
      if (!validateStep2()) return;
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    setErrors({});
    setSubmitError('');
    setStepError('');
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateStep3()) return;

    setLoading(true);
    setSubmitError('');

    try {
      // Check email uniqueness
      const emailExists = await companyRegistrationService?.checkEmailExists(adminData?.email);
      if (emailExists) {
        setErrors(prev => ({ ...prev, email: 'Este correo ya está registrado. Intenta iniciar sesión.' }));
        setLoading(false);
        return;
      }

      const { companyId } = await companyRegistrationService?.registerCompany({
        companyData,
        planId: selectedPlanId,
        billingCycle,
        adminData
      });

      // Clear draft on successful registration
      try { localStorage.removeItem(DRAFT_KEY); } catch (e) {}

      // Check if selected plan requires payment
      const selectedPlan = plans?.find(p => p?.id === selectedPlanId);
      const isPaid = companyRegistrationService?.isPaidPlan(selectedPlan);

      if (isPaid && import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY && import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY !== 'your-stripe-publishable-key-here') {
        await stripeService?.redirectToCheckout({
          companyId,
          planId: selectedPlanId,
          billingCycle,
        });
      } else {
        setSuccess(true);
        setTimeout(() => {
          navigate('/workshop-dashboard');
        }, 3000);
      }
    } catch (err) {
      console.error('Registration error:', err);
      setSubmitError(err?.message || 'Error al registrar la empresa. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="CheckCircle" className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">¡Registro Exitoso!</h2>
          <p className="text-gray-600 mb-2">Tu empresa ha sido registrada correctamente en GearControl.</p>
          <p className="text-sm text-gray-500 mb-6">Redirigiendo al dashboard en unos segundos...</p>
          <div className="flex items-center justify-center gap-2 text-blue-600">
            <Icon name="Loader2" className="w-5 h-5 animate-spin" />
            <span className="text-sm">Inicializando tu espacio de trabajo...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center">
              <Icon name="Wrench" className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">GearControl</h1>
              <p className="text-xs text-gray-500">Registro de Empresa</p>
            </div>
          </div>
          <Link
            to="/signin"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
          >
            <Icon name="LogIn" className="w-4 h-4" />
            Ya tengo cuenta
          </Link>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Registra tu Taller</h2>
          <p className="text-gray-600">Configura tu empresa en GearControl en 3 simples pasos</p>
        </div>

        <RegistrationStepIndicator currentStep={currentStep} />

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
          {/* Step 1: Company Info */}
          {currentStep === 1 && (
            <CompanyInfoForm
              formData={companyData}
              errors={errors}
              onChange={handleCompanyChange}
            />
          )}

          {/* Step 2: Plan Selection */}
          {currentStep === 2 && (
            plansLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <Icon name="Loader2" className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">Cargando planes disponibles...</p>
                </div>
              </div>
            ) : (
              <PlanSelectionPanel
                plans={plans}
                selectedPlanId={selectedPlanId}
                billingCycle={billingCycle}
                onPlanSelect={setSelectedPlanId}
                onBillingCycleChange={setBillingCycle}
                errors={errors}
              />
            )
          )}

          {/* Step 3: Admin Account */}
          {currentStep === 3 && (
            <AdminAccountForm
              formData={adminData}
              errors={errors}
              onChange={handleAdminChange}
            />
          )}

          {/* Step-level error (Step 1 timeout/network) */}
          {stepError && (
            <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2">
              <Icon name="AlertTriangle" className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-orange-800">{stepError}</p>
            </div>
          )}

          {/* Submit error message */}
          {submitError && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{submitError}</p>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
            <div>
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={loading || stepLoading}
                  className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <Icon name="ChevronLeft" className="w-4 h-4" />
                  Anterior
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400">Paso {currentStep} de 3</span>
              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={stepLoading}
                  className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-70"
                >
                  {stepLoading ? (
                    <>
                      <Icon name="Loader2" className="w-4 h-4 animate-spin" />
                      Verificando...
                    </>
                  ) : (
                    <>
                      Siguiente
                      <Icon name="ChevronRight" className="w-4 h-4" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Icon name="Loader2" className="w-4 h-4 animate-spin" />
                      Registrando empresa...
                    </>
                  ) : (
                    <>
                      <Icon name="CheckCircle" className="w-4 h-4" />
                      Completar Registro
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Summary panel when on step 3 */}
        {currentStep === 3 && (
          <div className="mt-6 bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Icon name="ClipboardList" className="w-4 h-4 text-blue-500" />
              Resumen del Registro
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-500 mb-1">Empresa</p>
                <p className="font-medium text-gray-800">{companyData?.companyName || '-'}</p>
                <p className="text-xs text-gray-500">{companyData?.rfc}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Plan Seleccionado</p>
                <p className="font-medium text-gray-800">
                  {plans?.find(p => p?.id === selectedPlanId)?.plan_name || '-'}
                </p>
                <p className="text-xs text-gray-500 capitalize">{billingCycle}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Ubicación</p>
                <p className="font-medium text-gray-800">{companyData?.city || '-'}</p>
                <p className="text-xs text-gray-500">{companyData?.state}</p>
              </div>
            </div>
          </div>
        )}

        <p className="text-center text-xs text-gray-500 mt-6">
          Al registrarte aceptas nuestros{' '}
          <span className="text-blue-600 cursor-pointer hover:underline">Términos de Servicio</span>
          {' '}y{' '}
          <span className="text-blue-600 cursor-pointer hover:underline">Política de Privacidad</span>
        </p>
      </div>
    </div>
  );
};

export default CompanyRegistration;
